import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ModalService } from '@boels/helpers';
import {
  Cart,
  CartModification,
  EntryGroupUpdate,
  GlobalMessageType,
  GroupOrderModification,
} from '@boels/models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { GlobalMessageFacade } from '../../global-message/facade/global-message.facade';
import { BoelsReCaptchaV3Service } from '../../recaptcha/boels-recaptcha.service';
import {
  addToCart,
  addToCartFailure,
  addToCartSuccess,
  createCart,
  createCartFailure,
  createCartSuccess,
  loadCart,
  loadCartFailure,
  loadCartSuccess,
  placeOrderChallenge,
  placeOrderFail,
  placeOrderRecaptcha,
  placeOrderSuccess,
  removeCart,
  removeFromCart,
  removeFromCartFailure,
  removeFromCartSuccess,
  setActiveCart,
  updateCart,
  updateCartDates,
  updateCartDatesSuccess,
  updateCartFailure,
  updateCartMode,
  updateCartModeFailure,
  updateCartModeSuccess,
  updateCartSuccess,
} from '../actions/cart.action';
import { CartGatewayService } from '../gateway/cart.gateway';
import {
  selectActiveCartId,
  selectCartState,
} from '../selectors/cart.selectors';

@Injectable({
  providedIn: 'any',
})
export class CartEffects {
  private actions$ = inject(Actions);
  private cartGateway = inject(CartGatewayService);
  private store = inject(Store);
  private router = inject(Router);
  private recaptchaService = inject(BoelsReCaptchaV3Service, {
    optional: true,
  });
  private globalMessageService = inject(GlobalMessageFacade);
  private modalService = inject(ModalService);

  reloadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        removeFromCartSuccess,
        updateCartSuccess,
        addToCartSuccess,
        updateCartModeSuccess,
      ),
      withLatestFrom(this.store.select(selectCartState)),
      filter(([, cartState]) => cartState.processCount === 0),
      map(() => {
        return loadCart();
      }),
    ),
  );

  loadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCart),
      switchMap(() => {
        return this.cartGateway.getCart().pipe(
          map((cart: Cart) => loadCartSuccess({ cart, cartId: cart.guid })),
          catchError((errorMessage: string) =>
            of(loadCartFailure({ errorMessage })),
          ),
        );
      }),
    ),
  );

  setActiveCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCartSuccess),
      map((action) => {
        return setActiveCart({ activeCartId: action.cartId });
      }),
    ),
  );

  createCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createCart),
      switchMap(() =>
        this.cartGateway.createCart().pipe(
          take(1),
          map((cart: Cart) => createCartSuccess({ cart, cartId: cart.guid })),
          map(() => loadCart()),
          catchError((errorMessage: string) =>
            of(createCartFailure({ errorMessage })),
          ),
        ),
      ),
    ),
  );

  addToCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addToCart),
      switchMap((entry) =>
        this.cartGateway.addEntry(entry).pipe(
          map((cartModification: GroupOrderModification) =>
            addToCartSuccess({ cartModifications: cartModification }),
          ),
          catchError((errorMessage: string) => {
            return of(addToCartFailure({ errorMessage }));
          }),
        ),
      ),
    ),
  );

  removeEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(removeFromCart),
      map(({ entryNumber }) => entryNumber),
      switchMap((entryNumber) =>
        this.cartGateway.removeEntry(entryNumber).pipe(
          map((cart: Cart) => removeFromCartSuccess({ cart })),
          catchError((errorMessage: string) =>
            of(removeFromCartFailure({ errorMessage })),
          ),
        ),
      ),
    ),
  );

  updateEntry$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCart),
      switchMap((entryUpdate: EntryGroupUpdate) =>
        this.cartGateway.updateEntry(entryUpdate).pipe(
          map((cartModifications: GroupOrderModification) =>
            updateCartSuccess({ cartModifications: cartModifications }),
          ),
          catchError((errorMessage: string) =>
            of(updateCartFailure({ errorMessage })),
          ),
        ),
      ),
    ),
  );

  cartUpdatePointOfService$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCartMode),
      switchMap((action) =>
        this.cartGateway
          .updateCartPointOfService(
            action.pointOfServiceName,
            action.useRentalWall,
          )
          .pipe(
            map((cartModification: CartModification) =>
              updateCartModeSuccess({ cartModifications: cartModification }),
            ),
            catchError((errorMessage: string) =>
              of(updateCartModeFailure({ errorMessage })),
            ),
          ),
      ),
    ),
  );

  customPlaceOrderChallenge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(placeOrderChallenge),
      switchMap((action) => {
        const { termsChecked, remarks, newsletter } = action;
        const requests: Array<Observable<unknown>> = [];

        if (remarks) {
          requests.push(this.cartGateway.postRemarks(remarks));
        }

        if (newsletter) {
          requests.push(this.cartGateway.postNewsletter(newsletter));
        }

        if (requests.length === 0) {
          requests.push(of(null)); // placeholder to prevent empty array for combineLatest
        }

        return combineLatest(requests).pipe(
          switchMap(() => {
            if (!this.recaptchaService) return EMPTY;
            return this.recaptchaService.execute('placeOrder').pipe(
              mergeMap((recaptchaToken) => [
                placeOrderRecaptcha({
                  termsChecked,
                  recaptchaToken,
                  newsletter,
                }),
              ]),
              catchError((error) => of(placeOrderFail(error))),
            );
          }),
          catchError((error) => of(placeOrderFail(error))),
        );
      }),
    ),
  );

  customPlaceOrderRecaptcha$ = createEffect(() =>
    this.actions$.pipe(
      ofType(placeOrderRecaptcha),
      withLatestFrom(this.store.select(selectActiveCartId)),
      mergeMap(([action, cartId]) => {
        return this.cartGateway
          .placeOrderWithRecaptcha(action.termsChecked, action.recaptchaToken)
          .pipe(
            switchMap((order) => [
              removeCart({ cartId }),
              placeOrderSuccess({ order, newsletter: action.newsletter }),
            ]),
            tap(() => {
              this.router.navigateByUrl('/order-confirmation');
              this.modalService.close();
            }),
            catchError((error) => {
              this._handleErrorAndCloseModal();
              return of(placeOrderFail(error));
            }),
          );
      }),
    ),
  );

  updateCartDates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateCartDates),
      switchMap((action) => {
        if (action.startDate && action.endDate) {
          return this.cartGateway
            .updateCartDates(action.startDate, action.endDate)
            .pipe(
              map((cart: Cart) => {
                return updateCartDatesSuccess({
                  startDate: cart.startDate,
                  endDate: cart.endDate,
                });
              }),
            );
        }
        return EMPTY;
      }),
    ),
  );

  private _handleErrorAndCloseModal(): void {
    this.modalService.close();
    this.globalMessageService.add(
      { key: 'confirmation.error.text', scope: 'checkout' },
      GlobalMessageType.MSG_TYPE_ERROR,
    );
  }
}
